import { API, graphqlOperation } from "aws-amplify";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";

import ServiceOrderForm from "components/serviceOrders/ServiceOrderForm";
import {
  createExecutedServiceOrder,
  createServiceOrder,
  createServiceOrderEquipment,
  createServiceOrderLog,
  createServiceOrderMaintenanceRoutine,
  createServiceOrderTool,
  updateExecutedServiceOrder,
  updateServiceOrder,
  updateSpare,
} from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import { serviceOrderStatus } from "config/constants";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import moment from "moment";
import ConfirmModal from "components/utils/ConfirmModal";
import {
  executedServiceOrdersByServiceOrderID,
  getEquipment,
} from "graphql/queries";
import { convertToSlug } from "components/utils/functions";

const ServiceOrderCreateForm = ({ onSuccess, equipmentID }) => {
  const { me } = useContext(LayoutContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [modalConfirm, setModalConfirm] = useState({
    visible: false,
    values: null,
  });
  const [equipment, setEquipment] = useState();
  const serviceOrder = useMemo(
    () => ({
      description: "",
      billDate: "",
      billValue: "",
      billCurrency: "",
      companyID: equipment?.companyID || me.companyID || "",
      branchID: equipment?.branchID || "",
      costCenterID: "",
      color: "#14547b",
      observations: "",
      title: equipment?.name
        ? `${moment().format("YYYY/MM/DD")} - ${equipment.name}`
        : "",
      userExecutor: equipment?.id ? me.id : "",
      start: "",
      end: "",
      tools: [],
      spares: [],
      maintenanceRoutines: [],
      auxExecutors: [],
      geolocation: false,
      equipments: equipment?.id ? [equipment] : [],
      equipmentRoutines: [],
      equipmentSpares: [],
      photoRequired: false,
    }),
    [me.companyID, equipment, me.id],
  );

  const saveExecution = async (values) => {
    const executionData = await API.graphql(
      graphqlOperation(executedServiceOrdersByServiceOrderID, {
        serviceOrderID: values.serviceOrderID,
      }),
    );
    const executionItem =
      executionData.data.executedServiceOrdersByServiceOrderID.items[0];

    if (!executionItem) {
      await API.graphql({
        query: createExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            ...values,
          },
        },
      });
    } else {
      await API.graphql({
        query: updateExecutedServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            id: executionItem.id,
            ...values,
          },
        },
      });
    }
  };

  async function onCreateServiceOrder(valuesParam) {
    const {
      description,
      billDate,
      billValue,
      billCurrency,
      companyID,
      branchID,
      costCenterID,
      color,
      observations,
      title,
      userExecutor,
      start,
      end,
      tools,
      spares,
      auxExecutors,
      geolocation,
      equipments,
      equipmentSpares,
      photoRequired,
    } = valuesParam;
    let { equipmentRoutines, maintenanceRoutines } = valuesParam;

    let status =
      start && end ? serviceOrderStatus.scheduled : serviceOrderStatus.created;

    if (equipmentID) {
      status = serviceOrderStatus.executing;
      maintenanceRoutines?.forEach((routineID) => {
        equipmentRoutines.push({
          equipmentID,
          routineID,
        });
      });
      maintenanceRoutines = [];
    }

    const serviceOrderCreated = (
      await API.graphql({
        query: createServiceOrder.replaceAll("__typename", ""),
        variables: {
          input: {
            code: "temp-code",
            description,
            billDate,
            billValue,
            billCurrency,
            companyID,
            branchID,
            costCenterID,
            color,
            observations,
            title,
            userExecutor: userExecutor || null,
            auxExecutors,
            status,
            start: start || null,
            end: end || null,
            geolocation,
            equipmentRoutines,
            equipmentSpares,
            photoRequired,
            slug: title && convertToSlug(title),
          },
        },
      })
    ).data.createServiceOrder;

    await API.graphql({
      query: updateServiceOrder.replaceAll("__typename", ""),
      variables: {
        input: {
          id: serviceOrderCreated.id,
          code: serviceOrderCreated.id.substring(0, 6),
        },
      },
    });

    await API.graphql({
      query: createServiceOrderLog.replaceAll("__typename", ""),
      variables: {
        input: {
          serviceOrderID: serviceOrderCreated.id,
          status,
          assignedExecutor: userExecutor,
          userID: me.id,
          companyID,
        },
      },
    });

    const promises = [];

    promises.push(
      ...tools?.map((toolId) =>
        API.graphql({
          query: createServiceOrderTool.replaceAll("__typename", ""),
          variables: {
            input: {
              serviceOrderId: serviceOrderCreated.id,
              toolId,
            },
          },
        }),
      ),
    );

    promises.push(
      ...spares?.map((spareId) =>
        API.graphql({
          query: updateSpare.replaceAll("__typename", ""),
          variables: {
            input: {
              id: spareId,
              serviceOrderID: serviceOrderCreated.id,
            },
          },
        }),
      ),
    );

    promises.push(
      ...maintenanceRoutines?.map((maintenanceRoutineId) =>
        API.graphql({
          query: createServiceOrderMaintenanceRoutine.replaceAll(
            "__typename",
            "",
          ),
          variables: {
            input: {
              serviceOrderId: serviceOrderCreated.id,
              maintenanceRoutineId,
            },
          },
        }),
      ),
    );

    promises.push(
      ...equipments.reduce((promises, equipment) => {
        promises.push(
          API.graphql({
            query: createServiceOrderEquipment.replaceAll("__typename", ""),
            variables: {
              input: {
                serviceOrderId: serviceOrderCreated.id,
                equipmentId: equipment.id,
              },
            },
          }),
        );
        return promises;
      }, []),
    );

    await Promise.all(promises);

    await saveExecution({
      serviceOrderID: serviceOrderCreated.id,
      userExecutor: me.id,
      executedRoutines: [],
      observations: "",
      executionStartedAt: new Date(),
    });

    return serviceOrderCreated;
  }

  const confirmCreate = () => {
    onSubmit(modalConfirm.values, true);
    setModalConfirm({ visible: false });
  };

  async function onSubmit(valuesParam, force) {
    try {
      const { start, end, serie, times, timeInterval, until } = valuesParam;

      setLoading(true);

      const dates = [];
      if (serie && start && end) {
        const untilDate = moment(until);

        let currentStartDate = moment(start);
        let currentEndDate = moment(end);
        while (currentStartDate.isSameOrBefore(untilDate, "day")) {
          dates.push({
            start: currentStartDate.toDate(),
            end: currentEndDate.toDate(),
          });
          currentStartDate.add(times || 1, timeInterval || "day");
          currentEndDate.add(times || 1, timeInterval || "day");
        }
      }

      let serviceOrderId = null;

      if (dates.length > 0) {
        if (!force) {
          setModalConfirm({
            visible: true,
            values: valuesParam,
            quantity: dates.length,
          });
          return;
        }
        for (const date of dates) {
          const serviceOrderCreated = await onCreateServiceOrder({
            ...valuesParam,
            start: date.start,
            end: date.end,
          });
          serviceOrderId = serviceOrderCreated?.id;
        }
      } else {
        const serviceOrderCreated = await onCreateServiceOrder(valuesParam);
        serviceOrderId = serviceOrderCreated?.id;
      }

      toast.success(strings.successAdded);
      if (onSuccess) onSuccess(serviceOrderId);
      else navigate("/service-orders");
    } catch (err) {
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
    }
  }

  async function fetchEquipment(id) {
    try {
      setLoading(true);
      const equipmentData = await API.graphql(
        graphqlOperation(getEquipment, { id }),
      );
      const equipmentItem = equipmentData.data.getEquipment;
      if (!equipmentItem) {
        toast.error(strings.responses.errorFetch);
        navigate("/equipments");
        return;
      }
      setEquipment(equipmentItem);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (equipmentID) fetchEquipment(equipmentID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentID]);

  return (
    <div>
      <ServiceOrderForm
        onSubmit={onSubmit}
        initialValues={serviceOrder}
        fromEquipment={!!equipmentID}
      />
      <ConfirmModal
        visible={modalConfirm.visible}
        body={strings.formatString(strings.modal.confirmCreateQuestion, {
          quantity: modalConfirm.quantity,
        })}
        onCancel={() => setModalConfirm({ visible: false })}
        onConfirm={confirmCreate}
      />
      {loading && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Agregar Orden de Servicio",
    successAdded: "Orden de Servicio agregada con éxito",
    responses: {
      errorFetch: "Error al obtener la información del equipo",
      unexpectedError: "Ha ocurrido un error inesperado",
    },
    validations: {},
    modal: {
      confirmCreateQuestion:
        "Se crearán {quantity} órdenes en total ¿Estás seguro de realizar la acción?",
    },
  },
});

export default ServiceOrderCreateForm;
